import { useState } from 'react';
import { toast } from 'sonner';
import { ModalShell } from '../ui/ModalShell';
import { Button } from '../ui/Button';
import { useOnboardingCatalog } from '../../hooks/useOnboardingTasks';
import { useOnboardingForms } from '../../hooks/useOnboardingForms';
import { useOnboardingDocuments } from '../../hooks/useOnboardingDocuments';
import { useTrainingModules } from '../../hooks/useTrainingModules';
import { useBulkAssign } from '../../hooks/onboardingOps';
import { ApiError } from '../../lib/api';

type ItemType = 'document' | 'form' | 'task' | 'training_module';
type ItemKey = `${ItemType}:${string}`;

/**
 * Bulk-assign onboarding content to the people ticked on the dashboard. Pick
 * any mix of catalog tasks, forms and documents, and/or apply each person's
 * role template. Assignment is idempotent server-side, so re-running is safe.
 */
export function BulkAssignModal({
  personIds,
  onClose,
}: {
  personIds: string[];
  onClose: () => void;
}) {
  const catalog = useOnboardingCatalog();
  const forms = useOnboardingForms();
  const documents = useOnboardingDocuments();
  const trainingModules = useTrainingModules();
  const bulk = useBulkAssign();

  const [selected, setSelected] = useState<Set<ItemKey>>(new Set());
  const [applyTemplate, setApplyTemplate] = useState(false);

  function toggle(key: ItemKey) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }

  const tasks = (catalog.data ?? []).filter((t) => !t.archived_at);
  // Forms / documents are only assignable once they have a published version.
  // Desk checklists (kit handover) are never assignable as tasks.
  const publishedForms = (forms.data ?? []).filter(
    (f) =>
      !f.archived_at && f.kind !== 'kit_handover' && f.versions.some((v) => v.published_at),
  );
  const publishedDocs = (documents.data ?? []).filter(
    (d) => !d.archived_at && d.versions.some((v) => v.published_at),
  );
  const publishedModules = (trainingModules.data ?? []).filter(
    (m) => !m.archived_at && m.versions.some((v) => v.published_at),
  );

  const count = selected.size + (applyTemplate ? 1 : 0);

  async function submit() {
    const items = [...selected].map((k) => {
      const [type, id] = k.split(/:(.+)/) as [ItemType, string];
      return { type, id };
    });
    try {
      const r = await bulk.mutateAsync({
        person_ids: personIds,
        items: items.length ? items : undefined,
        apply_template: applyTemplate || undefined,
      });
      toast.success(
        r.assigned > 0
          ? `Assigned ${r.assigned} task${r.assigned === 1 ? '' : 's'} across ${r.people} ${r.people === 1 ? 'person' : 'people'}`
          : 'Nothing new to assign (already up to date)',
      );
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Bulk assign failed');
    }
  }

  return (
    <ModalShell open onClose={onClose} titleId="bulk-assign-title" maxWidth="max-w-lg" busy={bulk.isPending}>
      <h2 id="bulk-assign-title" className="mb-1 text-base font-semibold text-fg">
        Assign tasks to {personIds.length} {personIds.length === 1 ? 'person' : 'people'}
      </h2>
      <p className="mb-4 text-xs text-fg-dim">
        Pick what to assign. Everything is idempotent, so people who already have an item keep just one copy.
      </p>

      <div className="max-h-[55vh] space-y-4 overflow-y-auto pr-1">
        <label className="flex items-start gap-2 rounded-token-sm border border-line/10 bg-surface-2 p-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5"
            checked={applyTemplate}
            onChange={(e) => setApplyTemplate(e.target.checked)}
          />
          <span>
            <span className="font-medium text-fg">Apply each person's role template</span>
            <span className="mt-0.5 block text-xs text-fg-dim">
              Assigns the full task list configured for each person's role. People with no role or no template are skipped.
            </span>
          </span>
        </label>

        <ItemGroup title="Tasks" empty="No catalog tasks yet" loading={catalog.isLoading}>
          {tasks.map((t) => (
            <ItemRow key={t.id} label={t.title} checked={selected.has(`task:${t.id}`)} onChange={() => toggle(`task:${t.id}`)} />
          ))}
        </ItemGroup>

        <ItemGroup title="Forms" empty="No published forms" loading={forms.isLoading}>
          {publishedForms.map((f) => (
            <ItemRow key={f.id} label={f.title} checked={selected.has(`form:${f.id}`)} onChange={() => toggle(`form:${f.id}`)} />
          ))}
        </ItemGroup>

        <ItemGroup title="Documents to sign" empty="No published documents" loading={documents.isLoading}>
          {publishedDocs.map((d) => (
            <ItemRow key={d.id} label={d.title} checked={selected.has(`document:${d.id}`)} onChange={() => toggle(`document:${d.id}`)} />
          ))}
        </ItemGroup>

        <ItemGroup title="Training modules" empty="No published training modules" loading={trainingModules.isLoading}>
          {publishedModules.map((m) => (
            <ItemRow key={m.id} label={m.title} checked={selected.has(`training_module:${m.id}`)} onChange={() => toggle(`training_module:${m.id}`)} />
          ))}
        </ItemGroup>
      </div>

      <div className="mt-5 flex justify-end gap-2">
        <Button variant="ghost" size="md" onClick={onClose} disabled={bulk.isPending}>
          Cancel
        </Button>
        <Button variant="primary" size="md" onClick={submit} loading={bulk.isPending} disabled={count === 0}>
          Assign{count > 0 ? ` (${count})` : ''}
        </Button>
      </div>
    </ModalShell>
  );
}

function ItemGroup({
  title,
  empty,
  loading,
  children,
}: {
  title: string;
  empty: string;
  loading: boolean;
  children: React.ReactNode;
}) {
  const items = Array.isArray(children) ? children : [children];
  const hasItems = items.some(Boolean) && items.length > 0;
  return (
    <div>
      <div className="mb-1.5 text-xs font-medium uppercase tracking-wider text-fg-dim">{title}</div>
      {loading ? (
        <p className="text-sm text-fg-muted">Loading…</p>
      ) : !hasItems ? (
        <p className="text-sm text-fg-dim">{empty}</p>
      ) : (
        <div className="space-y-1">{children}</div>
      )}
    </div>
  );
}

function ItemRow({ label, checked, onChange }: { label: string; checked: boolean; onChange: () => void }) {
  return (
    <label className="flex items-center gap-2 rounded-token-sm px-2 py-1.5 text-sm text-fg hover:bg-surface-2">
      <input type="checkbox" checked={checked} onChange={onChange} />
      {label}
    </label>
  );
}
